import {getUrlParams} from 'shared/utils/url-utils';
import type LoginService from '../login/login.service';
import type ActionRequestService from '../commons/services/action-request.service';
import type ModalConversationService from '../commons/conversation/modal-conversation.service';
import type FinishedResponseInteractionService from '../commons/services/finished-response-interaction.service';
import {isInterruptedActionResponse} from '../commons/conversation/modal-utils';
import type DefinedActionPerformer from '../commons/services/defined-action-performer';
import {AuthInterceptorRegistrar} from '../login/auth.interceptor-registrar';
import {UserInterceptorRegistrar} from '../user/user.interceptor-registrar';
import {SettingInterceptorRegistrar} from '../use-cases/settings/setting.interceptor-registrar';
import type ModalConversationInterceptorService from '../commons/services/modal-conversation-interceptor.service';
import type RecordService from '../record/record.service';
import type {IDocumentService, IHttpService, IOnInit} from 'angular';
import type {TransitionService} from '@uirouter/angularjs';
import type {Fond} from 'typings/portaro.be.types';
import type {AnyObject} from 'typings/portaro.fe.types';
import type ReloaderService from '../commons/services/reloader.service';
import type GlobalSearchService from '../commons/components/kp-global-search/kp-global-search.service';
import type MainConfigurationService from '../commons/main-configuration/main-configuration.service';
import type {SearchInputService} from '../commons/components/kp-search-input/search-input.provider';
import type {RecordKind} from 'typings/portaro.be.types';
import type {ModalWindowType} from '../commons/conversation/modal-conversation.service';

export class PageController implements IOnInit {
    public static controllerName = 'PageCtrl';

    public autofocusGlobalSearch = false;
    public getUrlParams = getUrlParams;
    public globalSearchInputEnabled;

    /*@ngInject*/
    constructor(private CONTEXT_PATH: string,
                private portaroVersion: AnyObject,
                private loginService: LoginService,
                private mainConfigurationService: MainConfigurationService,
                private actionRequestService: ActionRequestService,
                private modalConversationService: ModalConversationService,
                private finishedResponseInteractionService: FinishedResponseInteractionService,
                private definedActionPerformer: DefinedActionPerformer,
                private modalConversationInterceptor: ModalConversationInterceptorService,
                private recordService: RecordService,
                private $http: IHttpService,
                private $transitions: TransitionService,
                private reloader: ReloaderService,
                private globalSearchService: GlobalSearchService,
                private searchInputService: SearchInputService,
                private $document: IDocumentService,
    ) {
        definedActionPerformer.registerActionRequestAction((path, requestMethod, requestBody) => actionRequestService.action(path, requestMethod, requestBody));

        definedActionPerformer.registerMappedAction('record-holding-creation', (action) => recordService.createRecordHoldingAndAddExemplarsThrowingToModal(action.params?.kind as RecordKind));
        definedActionPerformer.registerMappedAction('record-holding-creation-no-record-presearch', (action) => recordService.createRecordHoldingAndAddExemplarsThrowingToModal(action.params?.kind as RecordKind, false));
        definedActionPerformer.registerMappedAction('record-creation', async (action) => recordService.createNewRecord({
            kind: action.params?.kind as RecordKind,
            fond: action.params?.fond as Fond
        }));

        new AuthInterceptorRegistrar(modalConversationInterceptor, loginService, $http).register();
        new UserInterceptorRegistrar(modalConversationInterceptor).register();
        new SettingInterceptorRegistrar(modalConversationInterceptor).register();

        $transitions.onEnter({to: 'home'}, () => this.autofocusGlobalSearch = true);
        this.globalSearchInputEnabled = searchInputService.isEnabled();

        this.openDialogsInChain();
    }

    public triggerGlobalSearch() {
        return this.globalSearchService.search();
    }

    public skipToContent() {
        this.$document[0].querySelector('main')?.focus();
    }

    public async setLocale(language) {
        await this.$http.get(`${this.CONTEXT_PATH}/set/setLocale`, {params: {language}})
        this.reloader.reload()
    }

    public async $onInit() {
        await this.showLoginIfInParam();
        await this.doActionRequestIfInParam();
    }

    private async showLoginIfInParam() {
        const urlParams = getUrlParams();
        if (urlParams.showLogin) {
            const redirect = urlParams.redirect;
            if (redirect) {
                await this.loginService.goLogged(redirect);
            } else {
                await this.loginService.thisPageLogged();
            }
        }
    }

    private async doActionRequestIfInParam() {
        const urlParams = getUrlParams();
        if (urlParams.actionRequest) {
            const actionRequest = JSON.parse(urlParams.actionRequest);
            await this.actionRequestService.action(actionRequest.path, actionRequest.method, actionRequest.body);
        }
    }

    private async openDialogsInChain() {
        for (const dialog of window.dialogs) {
            try {
                const actionResponse = await this.modalConversationService.openModalWindowAndGetFinishedResponse(dialog.responseType as ModalWindowType, dialog, this.isComponent(dialog.responseType) ? {isComponent: true} : null);
                await this.finishedResponseInteractionService.showSuccessResponseInToastIfPossible(actionResponse);
            } catch (exceptionResponse) {
                if (!isInterruptedActionResponse(exceptionResponse)) {
                    this.finishedResponseInteractionService.showFailedResponseInToast(exceptionResponse);
                }
            }
        }
    }

    private isComponent(responseType): boolean {
        return responseType.match(/^[a-z][a-zA-Z0-9]+$/); // lower camelCase test
    }
}
